import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ContactInfo } from './user';
import ADD_PERMISSION from './mutations/addPermission';
import {
  AUTHENTICATE_WITH_PASSWORD,
  AuthenticateWithPasswordRequest,
} from './mutations/authenticateWithPassword';
import CREATE_USER from './mutations/createUser';
import {
  APTITUDE_USER_APTITUDE_ORGS,
  AptitudeUserAptitudeOrgsVariables,
} from './queries/aptitudeUserAptitudeOrgs';
import {
  GET_EMAIL_VALIDATION,
  GetEmailValidationVariables,
} from './queries/getEmailValidation';
import { GET_USER, GetUserVariables } from './queries/getUser';
import {
  OKTA_USER_APTITUDE_ORGS,
  OktaUserAptitudeOrgsVariables,
} from './queries/oktaUserAptitudeOrgs';
import {
  USER_PASSWORD_APTITUDE_ORGS,
  UserPasswordAptitudeOrgsVariables,
} from './queries/userPasswordAptitudeOrgs';
import REFRESH_APTITUDE_TOKEN from './mutations/refreshAptitudeToken';
import REMOVE_PERMISSION from './mutations/removePermission';
import RESET_PASSWORD from './mutations/resetPassword';
import {
  SWITCH_APTITUDE_ASSIGNED_ORG,
  SwitchAptitudeAssignedOrgVariables,
} from './mutations/switchAptitudeAssignedOrg';
import UPDATE_CONTACT_INFO from './mutations/updateContactInfo';
import UPDATE_USER_NOTIFICATIONS from './mutations/updateUserNotifications';
import UPDATE_USER_ORG_ACCESS from './mutations/updateUserOrgAccess';
import UPDATE_USER_STATUS from './mutations/updateUserStatus';
import { ApolloInstances } from '../graphql.module';
import {
  AuthenticateFromOktaRequest,
  AUTHENTICATE_FROM_OKTA,
} from './mutations/authenticateFromOkta';
import { OrganizationList } from '../../api/user-api.service';
import { AssignedOrgs, User, UserNotification } from '../../../types/user/user';
import { HttpHeaders } from '@angular/common/http';

// This is how the backend expects the permission request
interface PermissionType {
  permissionType: string;
}

interface AuthenticationResponse {
  accountInformation?: {
    accountStatus: string;
  };
  mustAcceptTermsOfUse: boolean;
  token?: string;
  username?: string;
}

const NO_USER_RETURNED_ERROR = 'BACKEND ISSUE - no data returned';

@Injectable({
  providedIn: 'root',
})
export class UserGraphqlService {
  constructor(private apollo: Apollo) {}

  authenticateFromOkta(
    aptitudeOrgId: number | undefined,
    vizientBuyerOrgId: string | undefined,
    vizientSellerOrgId: string | undefined,
    oktaToken: string,
    acceptedTermsOfUse: boolean | undefined,
  ) {
    const variables: AuthenticateFromOktaRequest = { oktaToken };

    // Validate that we got at least one of the three possible input IDs.
    if (
      typeof aptitudeOrgId !== 'undefined' ||
      vizientBuyerOrgId ||
      vizientSellerOrgId
    ) {
      variables.aptitudeOrgId = aptitudeOrgId;
      variables.vizientBuyerOrgId = vizientBuyerOrgId;
      variables.vizientSellerOrgId = vizientSellerOrgId;
    } else {
      throw new Error('No org id provided');
    }

    acceptedTermsOfUse
      ? (variables['acceptedTermsOfUse'] = acceptedTermsOfUse)
      : null;

    return this.apollo
      .use(ApolloInstances.AUTH)
      .mutate<{ authenticateFromOkta: AuthenticationResponse }>({
        fetchPolicy: 'no-cache',
        variables,
        mutation: AUTHENTICATE_FROM_OKTA,
      })
      .pipe(
        take(1),
        map((result) => {
          if (result.data) {
            return result.data.authenticateFromOkta;
          } else {
            throw new Error(NO_USER_RETURNED_ERROR);
          }
        }),
      );
  }

  authenticateWithPassword(
    aptitudeOrgId: number,
    username: string,
    password: string,
    acceptedTermsOfUse: boolean | undefined,
  ): Observable<AuthenticationResponse> {
    const variables: AuthenticateWithPasswordRequest = {
      aptitudeOrgId,
      username,
      password,
    };

    if (acceptedTermsOfUse) {
      variables.acceptedTermsOfUse = acceptedTermsOfUse;
    }

    return this.apollo
      .use(ApolloInstances.AUTH)
      .mutate<{ authenticateWithPassword: AuthenticationResponse }>({
        fetchPolicy: 'no-cache',
        variables,
        mutation: AUTHENTICATE_WITH_PASSWORD,
        context: {
          headers: new HttpHeaders().set('Skip-Auth', 'true'),
        },
      })
      .pipe(
        map((request) => {
          if (request.data) {
            return request.data.authenticateWithPassword;
          } else {
            throw new Error(NO_USER_RETURNED_ERROR);
          }
        }),
      );
  }

  aptitudeUserAptitudeOrgs(
    aptitudeToken: string,
  ): Observable<OrganizationList> {
    const variables: AptitudeUserAptitudeOrgsVariables = { aptitudeToken };

    return this.apollo
      .use(ApolloInstances.AUTH)
      .query<{ aptitudeUserAptitudeOrgs: [{ id: number; name: string }] }>({
        fetchPolicy: 'no-cache',
        variables,
        query: APTITUDE_USER_APTITUDE_ORGS,
      })
      .pipe(
        map((request) => {
          return request.data.aptitudeUserAptitudeOrgs.map((org) => {
            return { organization: org };
          });
        }),
      );
  }

  oktaUserAptitudeOrgs(oktaToken: string): Observable<OrganizationList> {
    const variables: OktaUserAptitudeOrgsVariables = { oktaToken };

    return this.apollo
      .use(ApolloInstances.AUTH)
      .query<{ oktaUserAptitudeOrgs: [{ id: number; name: string }] }>({
        fetchPolicy: 'no-cache',
        variables,
        query: OKTA_USER_APTITUDE_ORGS,
      })
      .pipe(
        map((request) => {
          return request.data.oktaUserAptitudeOrgs.map((org) => {
            return { organization: org };
          });
        }),
      );
  }

  passwordUserAptitudeOrgs(
    username: string,
    password: string,
  ): Observable<OrganizationList> {
    const variables: UserPasswordAptitudeOrgsVariables = { username, password };

    return this.apollo
      .use(ApolloInstances.AUTH)
      .query<{ userPasswordAptitudeOrgs: [{ id: number; name: string }] }>({
        fetchPolicy: 'no-cache',
        variables,
        query: USER_PASSWORD_APTITUDE_ORGS,
      })
      .pipe(
        map((response) => {
          return response.data.userPasswordAptitudeOrgs.map((org) => {
            return { organization: org };
          });
        }),
      );
  }

  refreshAptitudeToken(aptitudeToken: string): Observable<string | undefined> {
    return this.apollo
      .use(ApolloInstances.AUTH)
      .mutate<{ refreshAptitudeToken: AuthenticationResponse }>({
        fetchPolicy: 'no-cache',
        variables: { aptitudeToken },
        mutation: REFRESH_APTITUDE_TOKEN,
      })
      .pipe(
        take(1),
        map((request) => {
          if (request.data) {
            return request.data.refreshAptitudeToken.token;
          } else {
            throw new Error(NO_USER_RETURNED_ERROR);
          }
        }),
      );
  }

  switchAptitudeAssignedOrg(
    aptitudeToken: string,
    aptitudeOrgId: number,
  ): Observable<AuthenticationResponse> {
    const variables: SwitchAptitudeAssignedOrgVariables = {
      aptitudeOrgId,
      aptitudeToken,
    };

    return this.apollo
      .use(ApolloInstances.AUTH)
      .mutate<{ switchAptitudeAssignedOrg: AuthenticationResponse }>({
        fetchPolicy: 'no-cache',
        variables,
        mutation: SWITCH_APTITUDE_ASSIGNED_ORG,
      })
      .pipe(
        take(1),
        map((request) => {
          if (request.data) {
            return request.data.switchAptitudeAssignedOrg;
          } else {
            throw new Error(NO_USER_RETURNED_ERROR);
          }
        }),
      );
  }

  createUser(user: User): Observable<User> {
    const contactInfo: ContactInfo = {
      firstName: user.firstName!,
      lastName: user.lastName!,
      email: user.emailAddress,
      jobTitle: user.jobTitle!,
      phoneNumber: user.mobilePhone,
      faxNumber: user.fax,
    };

    const permissions: PermissionType[] = [];
    user.permissions?.forEach((permission: string) => {
      permissions.push({
        permissionType: permission,
      });
    });

    const assignedOrgs = user?.assignedOrgs?.allOrgsAssigned
      ? []
      : user?.assignedOrgs?.organizations.map(
          (organization) => organization.id,
        ) ?? [];

    return this.apollo
      .use(ApolloInstances.USER)
      .mutate<{ newUser: User }>({
        fetchPolicy: 'no-cache',
        variables: {
          contactInfo,
          permissions,
          notifications: user.subscriptions,
          allOrgsAssigned: user?.assignedOrgs?.allOrgsAssigned ?? false,
          organizations: assignedOrgs,
        },
        mutation: CREATE_USER,
      })
      .pipe(
        take(1),
        map((request) => {
          if (request.data) {
            return request.data.newUser;
          } else {
            throw new Error(NO_USER_RETURNED_ERROR);
          }
        }),
      );
  }

  resetUserPassword(email: string, recaptchaToken: string) {
    return this.apollo.use(ApolloInstances.AUTH).query({
      fetchPolicy: 'no-cache',
      variables: { email, recaptchaToken },
      query: RESET_PASSWORD,
    });
  }

  getUser(id: number): Observable<User> {
    const variables: GetUserVariables = { id };

    return this.apollo
      .use(ApolloInstances.USER)
      .watchQuery<{ user: User }>({
        fetchPolicy: 'no-cache',
        variables,
        query: GET_USER,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.user),
      );
  }

  updateContactInfo(
    userId: number,
    contactInfo: ContactInfo,
  ): Observable<User> {
    return this.apollo
      .use(ApolloInstances.USER)
      .mutate<{ updateUserContactInfo: User }>({
        fetchPolicy: 'no-cache',
        variables: {
          userId,
          contactInfo,
        },
        mutation: UPDATE_CONTACT_INFO,
      })
      .pipe(
        take(1),
        map((request) => {
          if (request.data) {
            return request.data.updateUserContactInfo;
          } else {
            throw new Error(NO_USER_RETURNED_ERROR);
          }
        }),
      );
  }

  updateUserStatus(userId: number, active: boolean): Observable<User> {
    return this.apollo
      .use(ApolloInstances.USER)
      .mutate<{ activateUser: User }>({
        fetchPolicy: 'no-cache',
        variables: {
          userId,
          activateUser: active,
        },
        mutation: UPDATE_USER_STATUS,
      })
      .pipe(
        take(1),
        map((request) => {
          if (request.data) {
            return request.data.activateUser;
          } else {
            throw new Error(NO_USER_RETURNED_ERROR);
          }
        }),
      );
  }

  updateUserOrgAccess(
    userId: number,
    orgAccess: AssignedOrgs,
  ): Observable<User> {
    const assignedOrgs = orgAccess.allOrgsAssigned
      ? []
      : orgAccess.organizations.map((organization) => organization.id);

    return this.apollo
      .use(ApolloInstances.USER)
      .mutate<{ updateAssignedOrganizations: User }>({
        fetchPolicy: 'no-cache',
        variables: {
          userId,
          allOrgsAssigned: orgAccess.allOrgsAssigned,
          organizations: assignedOrgs,
        },
        mutation: UPDATE_USER_ORG_ACCESS,
      })
      .pipe(
        take(1),
        map((request) => {
          if (request.data) {
            return request.data.updateAssignedOrganizations;
          } else {
            throw new Error(NO_USER_RETURNED_ERROR);
          }
        }),
      );
  }

  updateUserNotifications(
    userId: number,
    notifications: UserNotification[],
  ): Observable<User> {
    return this.apollo
      .use(ApolloInstances.USER)
      .mutate<{ updateNotification: User }>({
        fetchPolicy: 'no-cache',
        variables: {
          userId,
          notifications,
        },
        mutation: UPDATE_USER_NOTIFICATIONS,
      })
      .pipe(
        take(1),
        map((request) => {
          if (request.data) {
            return request.data.updateNotification;
          } else {
            throw new Error(NO_USER_RETURNED_ERROR);
          }
        }),
      );
  }

  addPermission(userId: number, permission: PermissionType): Observable<User> {
    return this.apollo
      .use(ApolloInstances.USER)
      .mutate<{ addPermission: User }>({
        fetchPolicy: 'no-cache',
        variables: {
          userId,
          permission: permission,
        },
        mutation: ADD_PERMISSION,
      })
      .pipe(
        take(1),
        map((request) => {
          if (request.data) {
            return request.data.addPermission;
          } else {
            throw new Error(NO_USER_RETURNED_ERROR);
          }
        }),
      );
  }

  removePermission(
    userId: number,
    permission: PermissionType,
  ): Observable<User> {
    return this.apollo
      .use(ApolloInstances.USER)
      .mutate<{ removePermission: User }>({
        fetchPolicy: 'no-cache',
        variables: {
          userId,
          permission: permission,
        },
        mutation: REMOVE_PERMISSION,
      })
      .pipe(
        take(1),
        map((request) => {
          if (request.data) {
            return request.data.removePermission;
          } else {
            throw new Error(NO_USER_RETURNED_ERROR);
          }
        }),
      );
  }

  getEmailValidation(emailAddress: string): Observable<User> {
    const variables: GetEmailValidationVariables = { emailAddress };

    return this.apollo
      .use(ApolloInstances.USER)
      .watchQuery<{ validateEmail: User }>({
        fetchPolicy: 'no-cache',
        variables,
        query: GET_EMAIL_VALIDATION,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.validateEmail),
      );
  }
}
